// src/components/BottomNav.tsx
import React, { useState, useEffect } from 'react';
import {
    BottomNavigation,
    BottomNavigationAction,
    Paper,
    Box
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import QuizIcon from '@mui/icons-material/Quiz';
import SettingsIcon from '@mui/icons-material/Settings';
import MapIcon from '@mui/icons-material/Map';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSubjectsNavigation } from '../contexts/SubjectsNavigationContext';

export const NAV_HEIGHT = 72;  // ← ここを他ファイルから参照します

const BottomNav: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { lastPath } = useSubjectsNavigation();

    // URLからappIdを取得（例: /chusho-app/subjects/... → chusho-app）
    const appId = location.pathname.split('/')[1] || 'chusho-app';

    const getInitialValue = () => {
        if (location.pathname === '/' || location.pathname === `/${appId}`) return 0;
        if (location.pathname.includes('/subjects')) return 1;
        if (location.pathname.includes('/ai-roadmap')) return 2;
        if (location.pathname.includes('/settings')) return 3;
        return 0;
    };

    const [value, setValue] = useState(getInitialValue());

    useEffect(() => {
        if (location.pathname === '/' || location.pathname === `/${appId}`) setValue(0);
        else if (location.pathname.includes('/subjects')) setValue(1);
        else if (location.pathname.includes('/ai-roadmap')) setValue(2);
        else if (location.pathname.includes('/settings')) setValue(3);
        else setValue(0);
    }, [location.pathname, appId]);

    const handleChange = (_: React.SyntheticEvent, newValue: number) => {
        console.log('🔵 BottomNav handleChange called');
        console.log('🔵 Current pathname:', location.pathname);
        console.log('🔵 appId:', appId);
        console.log('🔵 newValue:', newValue);

        setValue(newValue);
        let targetPath = '';
        switch (newValue) {
            case 0:
                targetPath = `/${appId}`;
                break;
            case 1:
                targetPath = `/${appId}/subjects`;
                break;
            case 2:
                targetPath = `/${appId}/ai-roadmap`;
                break;
            case 3:
                targetPath = `/${appId}/settings`;
                break;
            default:
                targetPath = `/${appId}`;
        }
        console.log('🔵 Navigating to:', targetPath);
        navigate(targetPath);
    };

    const commonSx = {
        flex: '1 1 0',
        minWidth: 0,
        maxWidth: 'none',
        color: 'rgba(255,255,255,0.8)',
        position: 'relative',
        '&.Mui-selected': {
            color: '#fff',
            backgroundColor: '#006400'
        }
    };

    // 縦線（区切り線）コンポーネント
    const Divider = () => (
        <Box
            sx={{
                width: '1px',
                height: '40%',
                alignSelf: 'center',
                backgroundColor: 'rgba(255,255,255,0.5)',
                flexShrink: 0,
            }}
        />
    );

    return (
        <>
            <Paper
                sx={{
                    position: 'fixed',
                    inset: `auto 0 0 0`,
                    paddingBottom: 'env(safe-area-inset-bottom)',
                    zIndex: 1200
                }}
                elevation={3}
                square
            >
                <BottomNavigation
                    value={value}
                    showLabels
                    sx={{
                        backgroundColor: 'primary.main',
                        minHeight: NAV_HEIGHT,
                        display: 'flex',
                        alignItems: 'stretch',
                        position: 'relative'
                    }}
                >
                    <BottomNavigationAction
                        label="ホーム"
                        value={0}
                        icon={<HomeIcon />}
                        onClick={() => {
                            console.log('🔵 ホームボタン clicked');
                            navigate(`/${appId}`);
                        }}
                        sx={commonSx}
                    />

                    <Divider />

                    <BottomNavigationAction
                        label="科目"
                        value={1}
                        icon={<QuizIcon />}
                        onClick={() => {
                            console.log('🔵 科目ボタン clicked');
                            navigate(`/${appId}/subjects`);
                        }}
                        sx={commonSx}
                    />

                    <Divider />

                    {/* AI Roadmap - Center, larger and highlighted */}
                    <BottomNavigationAction
                        label="AIロードマップ"
                        value={2}
                        icon={
                            <Box
                                sx={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: '50%',
                                    background: 'linear-gradient(45deg, #FF6B6B 30%, #FFD93D 90%)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0 4px 12px rgba(255, 107, 107, 0.4)',
                                }}
                            >
                                <MapIcon sx={{ color: 'white' }} />
                            </Box>
                        }
                        onClick={() => {
                            console.log('🔵 AI Roadmapボタン clicked');
                            navigate(`/${appId}/ai-roadmap`);
                        }}
                        sx={{
                            ...commonSx,
                            flex: '1.3 1 0',
                            fontSize: '0.95rem',
                            '&.Mui-selected': {
                                color: '#FFD93D',
                                backgroundColor: 'transparent'
                            }
                        }}
                    />

                    <Divider />

                    <BottomNavigationAction
                        label="設定"
                        value={3}
                        icon={<SettingsIcon />}
                        onClick={() => {
                            console.log('🔵 設定ボタン clicked');
                            navigate(`/${appId}/settings`);
                        }}
                        sx={commonSx}
                    />
                </BottomNavigation>
            </Paper>
        </>
    );
};

export default BottomNav;
